import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { storesAPI, adminAPI, usersAPI, purchaseAPI, menuAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { useAuth } from "../contexts/AuthContext";
import { fmtDate, fmtDateTime } from "../utils/dateUtils";
import { Plus, X, AlertTriangle, Package, ArrowDown, ArrowUp, ShoppingCart,
  Download, Upload, Search, UtensilsCrossed, Loader, CheckCircle, AlertCircle, Check, Clock, BarChart2, RefreshCw, ClipboardList } from "lucide-react";
import { exportCSV, parseCSV } from "../utils/csvUtils";
import { useConfirm } from "../hooks/useConfirm";


function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto animate-fade-in-up`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white z-10">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose} className="text-[#8A7E73] hover:text-[#3A322C]"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function ImportCSVButton({ onImport }) {
  const ref = useRef();
  return (
    <>
      <button onClick={() => ref.current.click()}
        className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
        <Upload size={14} /> Import
      </button>
      <input ref={ref} type="file" accept=".csv" className="hidden" onChange={e => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => onImport(parseCSV(ev.target.result));
        reader.readAsText(file);
        e.target.value = '';
      }} />
    </>
  );
}

const TABS = [
  { id: "daily",      label: "Daily Ration",            icon: ShoppingCart,   subKey: null                   },
  { id: "requisitions",label: "Requisitions",           icon: ClipboardList,  subKey: null                   },
  { id: "inward",     label: "Stock Inward",            icon: ArrowDown,      subKey: "stores_inward"        },
  { id: "issue",      label: "Stock Issued",            icon: ArrowUp,        subKey: "stores_issued"        },
  { id: "confirm",    label: "Pending Confirmation",    icon: CheckCircle,    subKey: null                   },
  { id: "stockcount", label: "Stock Count",             icon: BarChart2,      subKey: null                   },
  { id: "variances",  label: "Variances",               icon: AlertTriangle,  subKey: null                   },
  { id: "reject",     label: "Reject Stock",            icon: AlertTriangle,  subKey: "stores_reject"        },
  { id: "outward",    label: "Stock Outward",           icon: ArrowUp,        subKey: "stores_outward"       },
  { id: "alerts",     label: "Min Stock Alerts",        icon: AlertTriangle,  subKey: "stores_alerts"        },
  { id: "expiry",     label: "Expiry Alerts",           icon: Clock,          subKey: null                   },
  { id: "summary",    label: "Stock Summary",           icon: Package,        subKey: "stores_summary"       },
  { id: "indent",     label: "Purchase Requests",       icon: ShoppingCart,   subKey: "stores_purchase_req"  },
];

const STATUS_COLORS = {
  optimal: "bg-[#EDF2EE] text-[#637C64]",
  minimum: "bg-[#FDF4EB] text-[#D98032]",
  critical: "bg-[#FBEAE7] text-[#C2624E]",
};

const INDENT_STATUS = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-blue-100 text-blue-700",
  ordered: "bg-purple-100 text-purple-700",
  fulfilled: "bg-[#EDF2EE] text-[#637C64]",
};

export default function StoresManagement() {
  const [searchParams] = useSearchParams();
  const { confirm, ConfirmModal } = useConfirm();
  const { user } = useAuth();
  const isManager = user?.is_admin || ["Sr. Manager","Manager","Administration","General Manager","Asst. Manager","Semi- Admin","Supervisor"].includes(user?.role_name || "");
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "inward");
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setActiveTab(t);
  }, [searchParams]);
  // Fallback: if active tab is hidden by permissions, switch to first visible tab
  useEffect(() => {
    const visible = TABS.filter(t => !t.subKey || user?.is_admin || user?.permissions?.[`sub_${t.subKey}_view`] !== false);
    if (visible.length > 0 && !visible.find(t => t.id === activeTab)) setActiveTab(visible[0].id);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const [masterItems, setMasterItems] = useState([]);
  const { kitchens: ctxKitchens, selectedKitchen, changeKitchen: setSelectedKitchen } = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  // Fallback: direct fetch if context kitchens still empty
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) { setKitchens(ctxKitchens); return; }
    const _t = setTimeout(() => {
      if (kitchens.length > 0) return;
      import("../utils/api").then(({ usersAPI }) => {
        usersAPI.getKitchens().then(r => {
          const ks = r.data || [];
          if (ks.length > 0) setKitchens(ks);
        }).catch(() => {});
      });
    }, 800);
    return () => clearTimeout(_t);
  }, [ctxKitchens]);
  const [inwardRecords, setInwardRecords] = useState([]);
  const [issueRecords, setIssueRecords] = useState([]);
  const [stockSummary, setStockSummary] = useState([]);
  const [batchStock, setBatchStock] = useState([]);
  const [indents, setIndents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [expiryAlerts, setExpiryAlerts] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [inwardItemSearch, setInwardItemSearch] = useState(""); // separate search for inward modal
  const [pendingGateEntries, setPendingGateEntries] = useState([]);

  // Load pending gate entries when inward modal opens
  const loadPendingGate = async () => {
    try {
      const r = await storesAPI.getPendingGateEntries(selectedKitchen ? { kitchen_id: selectedKitchen } : {});
      setPendingGateEntries(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const importFromGate = (entry) => {
    setForm(f => ({
      ...f,
      vendor_name: entry.vendor_name || "",
      invoice_number: entry.invoice_number || "",
      date: entry.invoice_date || new Date().toISOString().split("T")[0],
      quantity: entry.actual_weight || "",
      unit_price: "",
      gate_entry_id: entry.id,
    }));
  };

  // Derived: currently selected item in inward modal
  const selectedInwardItem = form.item_id ? masterItems.find(i => i.id === form.item_id) : null;

  // Bulk issue from daily plan state
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkDate, setBulkDate] = useState(new Date().toISOString().split("T")[0]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkItems, setBulkItems] = useState([]); // [{item_id, item_name, unit, total_qty_needed, current_stock, is_sufficient, qty_to_issue, selected, is_emergency, emergency_status}]
  const [bulkIssuing, setBulkIssuing] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkPlanInfo, setBulkPlanInfo] = useState(null);
  const [bulkPlanDoc, setBulkPlanDoc] = useState(null); // raw plan document for emergency approval
  const [approvingEa, setApprovingEa] = useState(null);

  useEffect(() => { fetchBase(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTabData(); }, [activeTab, selectedKitchen]); // eslint-disable-line react-hooks/exhaustive-deps
  // Auto-load plan when modal opens
  useEffect(() => {
    if (bulkModal && selectedKitchen) {
      loadBulkPlan();
    }
  }, [bulkModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBase = async () => {
    const items = await adminAPI.getMasterItems();
    setMasterItems(items.data || []);
    // Kitchen is now managed by global KitchenContext
  };

  const fetchTabData = async () => {
    // Allow all tabs when "All Kitchens" is selected — backend handles empty kitchen_id
    if (!selectedKitchen && !["summary", "alerts", "indent", "expiry", "inward", "issue"].includes(activeTab)) return;
    setLoading(true);
    try {
      const params = selectedKitchen ? { kitchen_id: selectedKitchen } : {};
      if (activeTab === "inward") {
        const r = await storesAPI.getInward({ ...params, limit: 200 });
        setInwardRecords(r.data || []);
      } else if (activeTab === "issue") {
        const r = await storesAPI.getIssues({ ...params, limit: 200 });
        setIssueRecords(r.data || []);
      } else if (activeTab === "reject" || activeTab === "outward") {
        // Reject Stock and Stock Outward — coming soon, use issue records filtered
        const r = await storesAPI.getIssues({ ...params, limit: 200 });
        setIssueRecords(r.data || []);
      } else if (activeTab === "summary") {
        const [s, b] = await Promise.all([
          storesAPI.getStockSummary(params),
          storesAPI.getBatchStock(params)
        ]);
        setStockSummary(s.data || []);
        setBatchStock(b.data || []);
      } else if (activeTab === "alerts") {
        const r = await storesAPI.getMinStockAlerts(params);
        setAlerts(r.data || []);
      } else if (activeTab === "expiry") {
        const r = await storesAPI.getExpiringStock(params);
        setExpiryAlerts(r.data || []);
      } else if (activeTab === "indent") {
        const r = await storesAPI.getIndents(params);
        setIndents(r.data || []);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleInward = async () => {
    setError("");
    // Validate: perishable items need expiry date
    if (selectedInwardItem?.is_perishable && !form.expiry_date) {
      setError(`${selectedInwardItem.name} is a perishable item — Expiry Date is required.`);
      return;
    }
    try {
      await storesAPI.createInward({ ...form, kitchen_id: selectedKitchen });
      // If created from a gate entry, mark it as linked
      if (form.gate_entry_id) {
        await storesAPI.linkGateEntry(form.gate_entry_id).catch(() => {});
      }
      setModal(null); setForm({});
      fetchTabData();
      setSuccess("Stock inward recorded!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const handleIssue = async () => {
    setError("");
    try {
      await storesAPI.createIssue({ ...form, kitchen_id: selectedKitchen });
      setModal(null); setForm({});
      fetchTabData();
      setSuccess("Stock issued!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const handleIndent = async () => {
    setError("");
    try {
      await storesAPI.createIndent({ ...form, kitchen_id: selectedKitchen });
      setModal(null); setForm({});
      fetchTabData();
      setSuccess("Purchase request raised!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  // Load daily plan for bulk issue
  const loadBulkPlan = async () => {
    if (!selectedKitchen || !bulkDate) return;
    setBulkLoading(true); setBulkError(""); setBulkItems([]); setBulkPlanInfo(null); setBulkPlanDoc(null);
    try {
      const [calcRes, planRes] = await Promise.all([
        menuAPI.calculateStock(bulkDate, selectedKitchen),
        menuAPI.getPlanByDate(bulkDate, selectedKitchen),
      ]);
      const data = calcRes.data;
      const planDoc = planRes.data;
      if (!data || !data.ingredients || data.ingredients.length === 0) {
        setBulkError("No daily plan found for this date. Please create a Daily Plan in Kitchen & Menu first.");
        setBulkLoading(false); return;
      }
      setBulkPlanInfo(data);
      setBulkPlanDoc(planDoc);
      setBulkItems(data.ingredients.map(ing => ({
        master_item_id: ing.master_item_id,
        item_name: ing.item_name,
        unit: ing.unit,
        total_qty_needed: ing.total_qty_needed,
        current_stock: ing.current_stock,
        is_sufficient: ing.is_sufficient,
        deficit: ing.deficit || 0,
        qty_to_issue: ing.total_qty_needed,
        required_qty: ing.total_qty_needed,
        selected: !ing.is_emergency,   // don't pre-select emergency items
        is_emergency: ing.is_emergency || false,
        emergency_status: ing.emergency_status || null,
      })));
    } catch (e) { setBulkError(formatError(e)); }
    setBulkLoading(false);
  };

  const approveEmergencyInPlan = async (idx) => {
    if (!bulkPlanDoc?.id) return;
    setApprovingEa(idx);
    try {
      await menuAPI.approvePlanEmergency(bulkPlanDoc.id, idx);
      // Reload to get updated statuses
      await loadBulkPlan();
    } catch (e) { setBulkError(formatError(e)); }
    setApprovingEa(null);
  };

  const handleBulkIssue = async () => {
    const toIssue = bulkItems.filter(i => i.selected && i.qty_to_issue > 0);
    if (toIssue.length === 0) { setBulkError("Select at least one item to issue"); return; }
    setBulkIssuing(true); setBulkError("");
    try {
      const payload = {
        kitchen_id: selectedKitchen,
        plan_date: bulkDate,
        purpose: "Daily Menu Plan",
        items: toIssue.map(i => ({
          item_id: i.master_item_id,
          item_name: i.item_name,
          unit: i.unit,
          quantity: i.qty_to_issue,
          required_qty: i.required_qty || i.total_qty_needed,
          is_emergency: i.is_emergency || false,
          emergency_status: i.emergency_status || null,
        }))
      };
      const res = await storesAPI.bulkIssue(payload);
      setBulkModal(false);
      setBulkItems([]); setBulkPlanInfo(null); setBulkPlanDoc(null);
      fetchTabData();
      const blocked = res.data.blocked_count || 0;
      const variance = res.data.variance_count || 0;
      let msg = `Issued ${res.data.issued_count} items from daily plan!`;
      if (blocked) msg += ` (${blocked} emergency items blocked — pending approval)`;
      if (variance) msg += ` ⚠ ${variance} variance(s)`;
      setSuccess(msg);
      setTimeout(() => setSuccess(""), 5000);
    } catch (e) { setBulkError(formatError(e)); }
    setBulkIssuing(false);
  };

  const handleWriteOff = async (batchId, itemName, qty, unit) => {
    if (!await confirm(`Write off ${qty} ${unit} of expired ${itemName}? This will reduce stock by this amount.`)) return;
    try {
      await storesAPI.writeOffBatch(batchId);
      setSuccess(`Written off expired ${itemName} batch`);
      setTimeout(() => setSuccess(""), 3000);
      fetchTabData();
    } catch (e) { alert(formatError(e)); }
  };

  const handleRaiseIndentFromAlert = async (itemId, currentStock) => {
    // Open the indent modal pre-filled with this item (no window.prompt)
    setForm({ item_id: itemId, quantity_required: "" });
    setModal("indent");
    setError("");
  };

  // Import handlers
  const handleImportInward = async (rows) => {
    let ok = 0;
    for (const row of rows) {
      const item = masterItems.find(i => i.name?.toLowerCase() === (row.item_name || "").toLowerCase() || String(i.item_id) === String(row.item_id));
      if (!item) continue;
      try {
        await storesAPI.createInward({ item_id: item.id, quantity: parseFloat(row.quantity || 0), kitchen_id: selectedKitchen, supplier: row.supplier || "", bill_number: row.bill_number || "", date: row.date || null });
        ok++;
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    }
    setSuccess(`Imported ${ok} inward records`); setTimeout(() => setSuccess(""), 3000);
    fetchTabData();
  };

  const handleImportIssue = async (rows) => {
    let ok = 0;
    for (const row of rows) {
      const item = masterItems.find(i => i.name?.toLowerCase() === (row.item_name || "").toLowerCase() || String(i.item_id) === String(row.item_id));
      if (!item) continue;
      try {
        await storesAPI.createIssue({ item_id: item.id, quantity: parseFloat(row.quantity || 0), kitchen_id: selectedKitchen, purpose: row.purpose || "", menu_item: row.menu_item || "" });
        ok++;
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    }
    setSuccess(`Imported ${ok} issue records`); setTimeout(() => setSuccess(""), 3000);
    fetchTabData();
  };

  // Filter helpers
  const filteredInward = inwardRecords.filter(r => !search || r.item_name?.toLowerCase().includes(search.toLowerCase()) || r.supplier?.toLowerCase().includes(search.toLowerCase()));
  const filteredIssues = issueRecords.filter(r => !search || r.item_name?.toLowerCase().includes(search.toLowerCase()) || r.purpose?.toLowerCase().includes(search.toLowerCase()));
  const filteredSummary = stockSummary.filter(r => (!search || r.name?.toLowerCase().includes(search.toLowerCase())) && (!filterCategory || r.category === filterCategory));
  const filteredIndents = indents.filter(r => !search || r.item_name?.toLowerCase().includes(search.toLowerCase()));
  const CATEGORIES = ["Pulses", "Oils", "Vegetables", "Whole Spices", "Masalas", "Salt", "Vinegar", "Sweeteners", "Grains", "Rice", "Flour", "Others", "General"];

  const addBtn = (tab) => {
    if (tab === "inward") return <button onClick={() => { setForm({}); setModal("inward"); setError(""); loadPendingGate(); }} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1"><Plus size={14} /> Add</button>;
    if (tab === "issue") return <button onClick={() => { setForm({}); setModal("issue"); setError(""); }} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1"><Plus size={14} /> Issue</button>;
    if (tab === "indent") return <button onClick={() => { setForm({}); setModal("indent"); setError(""); }} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1"><Plus size={14} /> Raise Request</button>;
    return null;
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="stores-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Stores Management</h1>
      </div>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}

      {/* Tabs */}
      <div className="flex border-b border-[#E5E1DB] overflow-x-auto">
        {TABS.filter(t => !t.subKey || user?.is_admin || user?.permissions?.[`sub_${t.subKey}_view`] !== false).map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {/* Search/Filter + Actions */}
      {["inward", "issue", "summary", "indent"].includes(activeTab) && (
        <div className="flex gap-3 flex-wrap items-center">
          <div className="flex-1 relative min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
            <input placeholder="Search..." className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {activeTab === "summary" && (
            <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <div className="flex gap-2 ml-auto">
            {activeTab === "inward" && <ImportCSVButton onImport={handleImportInward} />}
            {activeTab === "issue" && <ImportCSVButton onImport={handleImportIssue} />}
            {activeTab === "issue" && (
              <button
                onClick={() => { setBulkModal(true); setBulkError(""); setBulkItems([]); setBulkPlanInfo(null); setBulkDate(new Date().toISOString().split("T")[0]); }}
                className="flex items-center gap-1.5 text-sm border-2 border-[#E8956D] rounded-lg px-3 py-2 text-[#E8956D] hover:bg-[#E8956D]/10 font-bold transition-colors"
                data-testid="issue-from-plan-btn"
              >
                <UtensilsCrossed size={14} /> Issue from Daily Plan
              </button>
            )}
            {activeTab === "inward" && (              <button onClick={() => exportCSV(filteredInward.map(r => ({ date: r.date, item_name: r.item_name, quantity: r.quantity, unit: r.unit, supplier: r.supplier, bill_number: r.bill_number })), "stock_inward")} className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]"><Download size={14} /> Export</button>
            )}
            {activeTab === "issue" && (
              <button onClick={() => exportCSV(filteredIssues.map(r => ({ date: r.date, item_name: r.item_name, quantity: r.quantity, unit: r.unit, purpose: r.purpose, menu_item: r.menu_item })), "stock_issues")} className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]"><Download size={14} /> Export</button>
            )}
            {activeTab === "summary" && (
              <button onClick={() => exportCSV(filteredSummary.map(r => ({ item_id: r.item_id, name: r.name, category: r.category, unit: r.unit, current_stock: r.current_stock, min_stock: r.min_stock, status: r.stock_status })), "stock_summary")} className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]"><Download size={14} /> Export</button>
            )}
            {activeTab === "indent" && (
              <button onClick={() => exportCSV(filteredIndents.map(r => ({ date: r.created_at, item_name: r.item_name, quantity_required: r.quantity_required, kitchen: r.kitchen_id, raised_by: r.raised_by_name, status: r.status })), "purchase_requests")} className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]"><Download size={14} /> Export</button>
            )}
            {addBtn(activeTab)}
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div> : (
        <>
          {/* Inward Records */}
          {activeTab === "inward" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Date", "Item", "Qty", "Supplier", "Bill No.", "Expiry", "Notes"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {filteredInward.map(r => {
                      const isExpired = r.expiry_date && new Date(r.expiry_date) < new Date();
                      const isNear = r.expiry_date && !isExpired && (new Date(r.expiry_date) - new Date()) < 30 * 86400000;
                      return (
                        <tr key={r.id} className={`hover:bg-[#FAFAF8] ${isExpired ? "bg-[#FBEAE7]/20" : isNear ? "bg-[#FDF4EB]/20" : ""}`}>
                          <td className="px-4 py-3 text-xs text-[#8A7E73]">{fmtDate(r.date)}</td>
                          <td className="px-4 py-3 font-medium text-[#3A322C]">{r.item_name}</td>
                          <td className="px-4 py-3 text-[#5C5147]">{r.quantity} {r.unit}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{r.supplier || "—"}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{r.bill_number || "—"}</td>
                          <td className="px-4 py-3">
                            {r.expiry_date ? (
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isExpired ? "bg-[#FBEAE7] text-[#C2624E]" : isNear ? "bg-[#FDF4EB] text-[#D98032]" : "bg-[#EDF2EE] text-[#637C64]"}`}>
                                {isExpired ? "EXPIRED " : ""}{r.expiry_date}
                              </span>
                            ) : <span className="text-[#C4BAAF]">—</span>}
                          </td>
                          <td className="px-4 py-3 text-[#8A7E73] text-xs">{r.notes || "—"}</td>
                        </tr>
                      );
                    })}
                    {filteredInward.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8A7E73]">No records found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Issue Records */}
          {activeTab === "issue" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Date", "Item", "Qty", "Purpose", "Menu Item", "Outside Menu"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {filteredIssues.map(r => (
                      <tr key={r.id} className="hover:bg-[#FAFAF8]">
                        <td className="px-4 py-3 text-xs text-[#8A7E73]">{fmtDate(r.date)}</td>
                        <td className="px-4 py-3 font-medium text-[#3A322C]">{r.item_name}</td>
                        <td className="px-4 py-3 text-[#5C5147]">{r.quantity} {r.unit}</td>
                        <td className="px-4 py-3 text-[#8A7E73]">{r.purpose || "—"}</td>
                        <td className="px-4 py-3 text-[#8A7E73]">{r.menu_item || "—"}</td>
                        <td className="px-4 py-3">{r.is_outside_menu && <span className="text-xs bg-[#FDF4EB] text-[#D98032] px-2 py-0.5 rounded-full font-bold">Yes</span>}</td>
                      </tr>
                    ))}
                    {filteredIssues.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8A7E73]">No records found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Reject Stock */}
          {activeTab === "reject" && (
            <div className="text-center py-20 bg-white border border-[#E5E1DB] rounded-2xl">
              <AlertTriangle size={40} className="mx-auto mb-3 text-[#C2624E] opacity-40" />
              <p className="font-bold text-[#3A322C]">Reject Stock</p>
              <p className="text-sm text-[#8A7E73] mt-1">Track stock rejected at gate or during quality check</p>
              <p className="text-xs text-[#C4BAAF] mt-2">Feature coming — will be added in the next update</p>
            </div>
          )}

          {/* Stock Outward */}
          {activeTab === "outward" && (
            <div className="text-center py-20 bg-white border border-[#E5E1DB] rounded-2xl">
              <ArrowUp size={40} className="mx-auto mb-3 text-[#8A7E73] opacity-40" />
              <p className="font-bold text-[#3A322C]">Stock Outward</p>
              <p className="text-sm text-[#8A7E73] mt-1">Track stock transferred out (not issued for meal use)</p>
              <p className="text-xs text-[#C4BAAF] mt-2">Feature coming — will be added in the next update</p>
            </div>
          )}

          {/* Alerts */}
          {activeTab === "alerts" && (
            <>
              <div className="flex justify-end">
                <button onClick={() => exportCSV(alerts.map(a => ({ name: a.name, category: a.category, unit: a.unit, current_stock: a.current_stock, min_stock: a.min_stock, deficit: a.deficit })), "min_stock_alerts")} className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]"><Download size={14} /> Export</button>
              </div>
              <div className="space-y-3">
                {alerts.length === 0 ? (
                  <div className="text-center py-16 text-[#637C64]"><Package size={48} className="mx-auto mb-3 opacity-30" /><p>All stock levels adequate</p></div>
                ) : alerts.map(item => (
                  <div key={item.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="font-bold text-[#3A322C]">{item.name}</p>
                      <p className="text-xs text-[#8A7E73]">{item.category}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.current_stock <= 0 ? STATUS_COLORS.critical : STATUS_COLORS.minimum}`}>
                          Current: {item.current_stock} {item.unit}
                        </span>
                        <span className="text-xs text-[#8A7E73]">Min: {item.min_stock} {item.unit}</span>
                        <span className="text-xs font-bold text-[#C2624E]">Deficit: {item.deficit} {item.unit}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => { setForm({ item_id: item.id }); setModal("indent"); setError(""); }}
                      className="flex items-center gap-1 text-xs bg-[#E8956D]/10 text-[#E8956D] px-3 py-2 rounded-lg font-bold hover:bg-[#E8956D]/20"
                    >
                      <ShoppingCart size={14} /> Raise Request
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Expiry Alerts */}
          {activeTab === "expiry" && (
            <>
              <div className="flex justify-end">
                <button onClick={() => exportCSV(expiryAlerts.map(a => ({ item: a.item_name, batch: a.batch_number, qty: a.quantity, unit: a.unit, expiry_date: a.expiry_date, best_before: a.best_before_date, days_left: a.days_left, supplier: a.supplier })), "expiry_alerts")}
                  className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]"><Download size={14} /> Export</button>
              </div>
              <div className="space-y-3">
                {expiryAlerts.length === 0 ? (
                  <div className="text-center py-16 text-[#637C64]"><CheckCircle size={48} className="mx-auto mb-3 opacity-30" /><p>No expiring stock in the next 30 days</p></div>
                ) : expiryAlerts.map(item => (
                  <div key={item.id} className={`bg-white border rounded-xl p-4 flex items-center justify-between ${item.is_expired ? "border-[#C2624E]" : item.alert_level === "critical" ? "border-[#D98032]" : "border-[#E5E1DB]"}`}>
                    <div>
                      <p className="font-bold text-[#3A322C]">{item.item_name}</p>
                      <p className="text-xs text-[#8A7E73]">Batch: {item.batch_number || "—"} · Qty: {item.quantity} {item.unit} · Supplier: {item.supplier || "—"}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        {item.expiry_date && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.is_expired ? "bg-[#FBEAE7] text-[#C2624E]" : item.alert_level === "critical" ? "bg-[#FDF4EB] text-[#D98032]" : "bg-yellow-50 text-yellow-700"}`}>
                          Expiry: {item.expiry_date} {item.is_expired ? "(EXPIRED)" : `(${item.days_left}d left)`}
                        </span>}
                        {item.best_before_date && <span className="text-xs text-[#8A7E73]">Best Before: {item.best_before_date}</span>}
                      </div>
                    </div>
                    <AlertTriangle size={18} className={item.is_expired ? "text-[#C2624E]" : "text-[#D98032]"} />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Stock Summary — Perishable + Non-Perishable */}
          {activeTab === "summary" && (
            <div className="space-y-6">
              {/* ── PERISHABLE SECTION ── */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">Perishable Items</h2>
                  <span className="text-xs bg-[#FDF4EB] text-[#D98032] px-2 py-0.5 rounded-full font-bold">FEFO Batch Tracking</span>
                  <span className="text-xs text-[#8A7E73]">— usable stock excludes expired batches</span>
                </div>

                {batchStock.filter(i => !filterCategory || i.category === filterCategory).length === 0 ? (
                  <p className="text-sm text-[#8A7E73] italic">No perishable items with stock data.</p>
                ) : (
                  <div className="space-y-3">
                    {batchStock
                      .filter(i => (!filterCategory || i.category === filterCategory) && (!search || i.name.toLowerCase().includes(search.toLowerCase())))
                      .map(item => {
                        const usableStatus = item.total_usable <= 0 ? "critical" : item.total_usable <= item.min_stock ? "minimum" : "optimal";
                        const hasBadBatches = item.batches.some(b => b.status !== "fresh" && b.quantity_remaining > 0);
                        return (
                          <div key={item.id} className={`bg-white border rounded-xl overflow-hidden ${hasBadBatches ? "border-[#D98032]" : "border-[#E5E1DB]"}`}>
                            {/* Item header */}
                            <div className="flex items-center justify-between px-4 py-3 bg-[#FAFAF8] border-b border-[#E5E1DB]">
                              <div className="flex items-center gap-3">
                                <div>
                                  <span className="font-bold text-[#3A322C]">{item.name}</span>
                                  <span className="text-xs text-[#8A7E73] ml-2">{item.category}</span>
                                </div>
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[usableStatus]}`}>
                                  {item.total_usable} {item.unit} usable
                                </span>
                                {item.total_expired > 0 && (
                                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#FBEAE7] text-[#C2624E]">
                                    {item.total_expired} {item.unit} expired
                                  </span>
                                )}
                              </div>
                              {item.min_stock > 0 && (
                                <span className="text-xs text-[#8A7E73]">Min: {item.min_stock} {item.unit}</span>
                              )}
                            </div>

                            {/* Batch rows */}
                            {item.batches.filter(b => b.quantity_remaining > 0).length > 0 ? (
                              <div className="divide-y divide-[#F4F3F0]">
                                {item.batches.filter(b => b.quantity_remaining > 0).map((batch, bi) => {
                                  const BATCH_COLORS = { fresh: "bg-[#EDF2EE] text-[#637C64]", warning: "bg-yellow-50 text-yellow-700", critical: "bg-[#FDF4EB] text-[#D98032]", expired: "bg-[#FBEAE7] text-[#C2624E]" };
                                  return (
                                    <div key={bi} className={`flex items-center justify-between px-4 py-2.5 ${batch.is_expired ? "bg-[#FBEAE7]/20" : batch.status === "critical" ? "bg-[#FDF4EB]/20" : ""}`}>
                                      <div className="flex items-center gap-4 flex-wrap">
                                        <div className="flex items-center gap-2">
                                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase ${BATCH_COLORS[batch.status]}`}>
                                            {batch.status}
                                          </span>
                                          <span className="font-semibold text-sm text-[#3A322C]">{batch.quantity_remaining} {item.unit}</span>
                                        </div>
                                        <div className="text-xs text-[#8A7E73] flex items-center gap-3">
                                          {batch.expiry_date && (
                                            <span className={batch.is_expired ? "text-[#C2624E] font-bold" : ""}>
                                              Exp: {batch.expiry_date}
                                              {batch.days_left !== null && (
                                                <span className="ml-1">
                                                  {batch.is_expired ? "(EXPIRED)" : `(${batch.days_left}d left)`}
                                                </span>
                                              )}
                                            </span>
                                          )}
                                          {batch.batch_number !== "—" && <span>Batch: {batch.batch_number}</span>}
                                          <span>Supplier: {batch.supplier}</span>
                                          <span className="text-[#C4BAAF]">Received: {batch.inward_date?.split("T")[0] || "—"}</span>
                                        </div>
                                      </div>
                                      {batch.is_expired && (
                                        <button
                                          onClick={() => handleWriteOff(batch.batch_id, item.name, batch.quantity_remaining, item.unit)}
                                          className="text-xs bg-[#FBEAE7] text-[#C2624E] border border-[#C2624E]/30 px-3 py-1.5 rounded-lg font-bold hover:bg-[#C2624E] hover:text-white transition-colors flex-shrink-0"
                                          data-testid="write-off-btn"
                                        >
                                          Write Off
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-[#C4BAAF] px-4 py-3">No remaining stock in any batch</p>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* ── NON-PERISHABLE SECTION ── */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">Non-Perishable Items</h2>
                  <span className="text-xs bg-[#EDF2EE] text-[#637C64] px-2 py-0.5 rounded-full font-bold">Running Total</span>
                </div>
                <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F3F0]">
                        <tr>{["#", "Item", "Category", "In Stock", "Min Stock", "Status"].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody className="divide-y divide-[#F4F3F0]">
                        {filteredSummary.filter(i => !i.is_perishable).map(item => (
                          <tr key={item.id} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 text-[#8A7E73]">{item.item_id}</td>
                            <td className="px-4 py-3 font-medium text-[#3A322C]">{item.name}</td>
                            <td className="px-4 py-3 text-[#8A7E73]">{item.category}</td>
                            <td className="px-4 py-3 font-bold">{item.current_stock} {item.unit}</td>
                            <td className="px-4 py-3 text-[#8A7E73]">{item.min_stock} {item.unit}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[item.stock_status]}`}>{item.stock_status}</span>
                            </td>
                          </tr>
                        ))}
                        {filteredSummary.filter(i => !i.is_perishable).length === 0 && (
                          <tr><td colSpan={6} className="text-center py-8 text-[#8A7E73]">No non-perishable items</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Purchase Requests (Indents) */}
          {activeTab === "indent" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Date", "Item", "Qty Required", "Raised By", "Status", "Notes"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {filteredIndents.map(r => (
                      <tr key={r.id} className="hover:bg-[#FAFAF8]">
                        <td className="px-4 py-3 text-xs text-[#8A7E73]">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3 font-medium text-[#3A322C]">{r.item_name}</td>
                        <td className="px-4 py-3 text-[#5C5147]">{r.quantity_required}</td>
                        <td className="px-4 py-3 text-[#8A7E73]">{r.raised_by_name || "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${INDENT_STATUS[r.status] || "bg-gray-100 text-gray-700"}`}>{r.status}</span>
                        </td>
                        <td className="px-4 py-3 text-[#8A7E73] text-xs">{r.notes || "—"}</td>
                      </tr>
                    ))}
                    {filteredIndents.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8A7E73]">No purchase requests</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {modal === "inward" && (
        <Modal title="Stock Inward Entry" onClose={() => { setModal(null); setInwardItemSearch(""); setForm({}); }}>
          <div className="space-y-4">
            {/* Import from Gate Entry */}
            {pendingGateEntries.length > 0 && (
              <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-xl p-3">
                <p className="text-xs font-bold uppercase text-[#637C64] mb-2 flex items-center gap-1">
                  ↓ Import from Verified Gate Entry
                </p>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {pendingGateEntries.map(e => (
                    <button key={e.id} onClick={() => importFromGate(e)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs border transition-colors ${form.gate_entry_id === e.id ? "bg-[#637C64] text-white border-[#637C64]" : "bg-white border-[#E5E1DB] hover:border-[#637C64] text-[#3A322C]"}`}>
                      <span className="font-bold">{e.vendor_name || "Unknown Vendor"}</span>
                      {e.invoice_number && <span className="ml-2 opacity-70">#{e.invoice_number}</span>}
                      {e.actual_weight && <span className="ml-2 text-[#637C64] font-bold">{e.actual_weight} kg</span>}
                      <span className="ml-2 opacity-60">{e.invoice_date || fmtDate(e.entry_time)}</span>
                    </button>
                  ))}
                </div>
                {form.gate_entry_id && (
                  <p className="text-[10px] text-[#637C64] mt-1.5 font-medium">✓ Pre-filled from gate entry. Select item & verify quantity below.</p>
                )}
              </div>
            )}
            {/* Item selection with search */}
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Search & Select Item *</label>
              <input
                placeholder="Type item name to filter..."
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] mb-1.5"
                value={inwardItemSearch}
                onChange={e => setInwardItemSearch(e.target.value)}
              />
              <select
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                value={form.item_id || ""}
                onChange={e => {
                  const item = masterItems.find(i => i.id === e.target.value);
                  const today = form.date || new Date().toISOString().split("T")[0];
                  // Auto-calculate expiry if perishable
                  let autoExpiry = "";
                  if (item?.is_perishable && item.default_shelf_life_days > 0) {
                    const d = new Date(today);
                    d.setDate(d.getDate() + item.default_shelf_life_days);
                    autoExpiry = d.toISOString().split("T")[0];
                  }
                  setForm(f => ({
                    ...f, item_id: e.target.value,
                    expiry_date: autoExpiry || f.expiry_date || "",
                    best_before_date: f.best_before_date || ""
                  }));
                }}
                required
              >
                <option value="">Select Item</option>
                {masterItems
                  .filter(i => !inwardItemSearch || i.name.toLowerCase().includes(inwardItemSearch.toLowerCase()))
                  .map(i => (
                    <option key={i.id} value={i.id}>
                      {i.item_id}. {i.name} ({i.category}){i.is_perishable ? " 🕐" : ""}
                    </option>
                  ))}
              </select>
            </div>

            {/* Perishable alert banner */}
            {selectedInwardItem?.is_perishable && (
              <div className="bg-[#FDF4EB] border border-[#D98032]/40 rounded-xl px-4 py-3 flex items-start gap-3">
                <AlertTriangle size={16} className="text-[#D98032] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-[#D98032]">Perishable Item — Expiry Date Required</p>
                  <p className="text-xs text-[#D98032]/80 mt-0.5">
                    Default shelf life: <strong>{selectedInwardItem.default_shelf_life_days} days</strong>.
                    Expiry date has been auto-filled — verify it matches the actual packaging.
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Quantity *</label>
              <input type="number" step="0.01"
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                value={form.quantity || ""}
                onChange={e => setForm({ ...form, quantity: parseFloat(e.target.value) })} required />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Supplier</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.supplier || ""} onChange={e => setForm({ ...form, supplier: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Bill Number</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.bill_number || ""} onChange={e => setForm({ ...form, bill_number: e.target.value })} />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Inward Date</label>
              <input type="date"
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                value={form.date || new Date().toISOString().split("T")[0]}
                onChange={e => {
                  const newDate = e.target.value;
                  // Recalculate expiry if perishable and shelf life set
                  let autoExpiry = form.expiry_date;
                  if (selectedInwardItem?.is_perishable && selectedInwardItem.default_shelf_life_days > 0) {
                    const d = new Date(newDate);
                    d.setDate(d.getDate() + selectedInwardItem.default_shelf_life_days);
                    autoExpiry = d.toISOString().split("T")[0];
                  }
                  setForm({ ...form, date: newDate, expiry_date: autoExpiry });
                }} />
            </div>

            {/* Batch & Expiry section — always visible for perishables, collapsible for others */}
            <div className={`rounded-xl border-2 p-4 space-y-3 transition-colors ${selectedInwardItem?.is_perishable ? "border-[#D98032] bg-[#FDF4EB]/30" : "border-[#E5E1DB]"}`}>
              <p className={`text-xs font-bold uppercase tracking-wide ${selectedInwardItem?.is_perishable ? "text-[#D98032]" : "text-[#8A7E73]"}`}>
                {selectedInwardItem?.is_perishable ? "Expiry Details *" : "Batch & Expiry (optional)"}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Batch / Lot No.</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="e.g. LOT-001" value={form.batch_number || ""} onChange={e => setForm({ ...form, batch_number: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Mfg. Date</label>
                  <input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.manufacturing_date || ""} onChange={e => setForm({ ...form, manufacturing_date: e.target.value })} />
                </div>
                <div>
                  <label className={`text-xs font-bold uppercase mb-1 block ${selectedInwardItem?.is_perishable ? "text-[#D98032]" : "text-[#8A7E73]"}`}>
                    Expiry Date {selectedInwardItem?.is_perishable ? "*" : ""}
                  </label>
                  <input type="date"
                    className={`w-full border-2 rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${selectedInwardItem?.is_perishable && !form.expiry_date ? "border-[#D98032] bg-[#FDF4EB]/50 focus:border-[#D98032]" : "border-[#E5E1DB] focus:border-[#E8956D]"}`}
                    value={form.expiry_date || ""} onChange={e => setForm({ ...form, expiry_date: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Best Before</label>
                  <input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.best_before_date || ""} onChange={e => setForm({ ...form, best_before_date: e.target.value })} />
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>

            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => { setModal(null); setInwardItemSearch(""); }} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleInward} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button>
            </div>
          </div>
        </Modal>
      )}

      {modal === "issue" && (
        <Modal title="Issue Stock" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Search Item *</label>
              <input placeholder="Type to filter..." className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] mb-1" onChange={e => setSearch(e.target.value)} />
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.item_id || ""} onChange={e => setForm({ ...form, item_id: e.target.value })} required>
                <option value="">Select Item</option>
                {masterItems.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase())).map(i => <option key={i.id} value={i.id}>{i.item_id}. {i.name} ({i.category})</option>)}
              </select>
            </div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Quantity *</label><input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.quantity || ""} onChange={e => setForm({ ...form, quantity: parseFloat(e.target.value) })} required /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Purpose</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.purpose || ""} onChange={e => setForm({ ...form, purpose: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Menu Item</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.menu_item || ""} onChange={e => setForm({ ...form, menu_item: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.is_outside_menu || false} onChange={e => setForm({ ...form, is_outside_menu: e.target.checked })} /> Issue outside regular menu (requires permission)</label>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3"><button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button><button onClick={handleIssue} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Issue</button></div>
          </div>
        </Modal>
      )}

      {modal === "indent" && (
        <Modal title="Raise Purchase Request" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Item *</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.item_id || ""} onChange={e => setForm({ ...form, item_id: e.target.value })} required>
                <option value="">Select Item</option>
                {masterItems.map(i => <option key={i.id} value={i.id}>{i.item_id}. {i.name}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Quantity Required *</label><input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.quantity_required || ""} onChange={e => setForm({ ...form, quantity_required: parseFloat(e.target.value) })} required /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3"><button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button><button onClick={handleIndent} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Submit Request</button></div>
          </div>
        </Modal>
      )}

      {/* ═══ BULK ISSUE FROM DAILY PLAN MODAL ═══ */}
      {bulkModal && (
        <Modal title="Issue Stock from Daily Plan" onClose={() => setBulkModal(false)} wide>
          <div className="space-y-4">
            {/* Date row */}
            <div className="flex gap-3 items-center flex-wrap">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
                <input type="date"
                  className="border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  value={bulkDate}
                  onChange={e => {
                    const d = e.target.value; setBulkDate(d);
                    setBulkItems([]); setBulkPlanInfo(null); setBulkPlanDoc(null); setBulkError("");
                    if (selectedKitchen) {
                      setBulkLoading(true);
                      Promise.all([menuAPI.calculateStock(d, selectedKitchen), menuAPI.getPlanByDate(d, selectedKitchen)])
                        .then(([calcRes, planRes]) => {
                          const data = calcRes.data; const planDoc = planRes.data;
                          if (!data?.ingredients?.length) { setBulkError("No daily plan found for this date. Create a plan in Kitchen & Menu → Daily Plan first."); }
                          else {
                            setBulkPlanInfo(data); setBulkPlanDoc(planDoc);
                            setBulkItems(data.ingredients.map(ing => ({
                              master_item_id: ing.master_item_id, item_name: ing.item_name, unit: ing.unit,
                              total_qty_needed: ing.total_qty_needed, current_stock: ing.current_stock,
                              is_sufficient: ing.is_sufficient, deficit: ing.deficit || 0,
                              qty_to_issue: ing.total_qty_needed, required_qty: ing.total_qty_needed,
                              selected: !ing.is_emergency, is_emergency: ing.is_emergency || false,
                              emergency_status: ing.emergency_status || null,
                            })));
                          }
                        }).catch(e => setBulkError(formatError(e))).finally(() => setBulkLoading(false));
                    }
                  }}
                />
              </div>
              {bulkPlanInfo && (
                <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold mt-4 ${bulkPlanInfo.all_sufficient ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
                  {bulkPlanInfo.all_sufficient ? <><CheckCircle size={15}/> All stock available</> : <><AlertCircle size={15}/> {bulkPlanInfo.deficit_count} item(s) below required</>}
                  {bulkPlanInfo.has_emergency && <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">Emergency items present</span>}
                </div>
              )}
              {!selectedKitchen && <p className="text-sm text-[#C2624E] mt-4 font-bold">Select a kitchen from the top menu first.</p>}
            </div>

            {bulkError && <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-xl px-4 py-3 text-sm text-[#C2624E]" data-testid="bulk-error">{bulkError}</div>}

            {bulkLoading && <div className="flex justify-center py-10"><Loader size={24} className="animate-spin text-[#E8956D]"/></div>}

            {!bulkLoading && bulkItems.length > 0 && (
              <>
                <div className="flex gap-2 flex-wrap text-xs">
                  <button onClick={() => setBulkItems(b => b.map(i => ({...i, selected: !i.is_emergency})))} className="border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-[#5C5147] hover:bg-[#F4F3F0] font-bold">Normal only</button>
                  <button onClick={() => setBulkItems(b => b.map(i => ({...i, selected: true})))} className="border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-[#5C5147] hover:bg-[#F4F3F0] font-bold">Select All</button>
                  <button onClick={() => setBulkItems(b => b.map(i => ({...i, selected: false})))} className="border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-[#5C5147] hover:bg-[#F4F3F0] font-bold">Deselect All</button>
                  <span className="ml-auto text-[#8A7E73] py-1">{bulkItems.filter(i => i.selected).length} of {bulkItems.length} selected</span>
                </div>

                <div className="border border-[#E5E1DB] rounded-xl overflow-hidden" data-testid="bulk-issue-table">
                  <div className="grid grid-cols-[28px_1fr_110px_100px_80px_100px] bg-[#F4F3F0] px-3 py-2.5 gap-2 text-xs font-bold uppercase text-[#5C5147]">
                    <span/><span>Ingredient</span><span className="text-right">Required</span><span className="text-right">In Stock</span><span>Status</span><span className="text-right">Issue Qty</span>
                  </div>
                  <div className="divide-y divide-[#F4F3F0] max-h-[360px] overflow-y-auto">
                    {bulkItems.map((item, idx) => {
                      const isPending = item.is_emergency && item.emergency_status !== "approved";
                      return (
                        <div key={idx} className={`grid grid-cols-[28px_1fr_110px_100px_80px_100px] px-3 py-2.5 gap-2 items-center transition-colors ${isPending ? "bg-orange-50/60" : item.selected ? "" : "opacity-50"} ${!isPending && !item.is_sufficient ? "bg-[#FBEAE7]/20" : ""}`} data-testid={`bulk-row-${idx}`}>
                          <input type="checkbox" checked={item.selected && !isPending} disabled={isPending}
                            onChange={e => setBulkItems(b => b.map((x,i) => i===idx ? {...x, selected: e.target.checked} : x))}
                            className="w-4 h-4 accent-[#E8956D] cursor-pointer"/>
                          <div>
                            <p className="text-sm font-medium text-[#3A322C]">{item.item_name}</p>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {item.is_emergency && (
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${item.emergency_status==="approved" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
                                  {item.emergency_status==="approved" ? "Emergency ✓" : "Emergency — Pending Approval"}
                                </span>
                              )}
                              {isPending && isManager && (
                                <button onClick={() => approveEmergencyInPlan(idx)} disabled={approvingEa===idx}
                                  className="text-[10px] bg-green-600 text-white px-2 py-0.5 rounded-full font-bold hover:bg-green-700 disabled:opacity-60"
                                  data-testid={`approve-ea-${idx}`}>
                                  {approvingEa===idx ? "…" : "Approve"}
                                </button>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-bold text-[#5C5147] text-right">{item.total_qty_needed} <span className="text-xs font-normal text-[#8A7E73]">{item.unit}</span></span>
                          <span className={`text-sm font-semibold text-right ${item.is_sufficient ? "text-[#637C64]" : "text-[#C2624E]"}`}>{item.current_stock} <span className="text-xs font-normal text-[#8A7E73]">{item.unit}</span></span>
                          {isPending
                            ? <span className="text-[10px] font-bold text-orange-700 bg-orange-100 px-1.5 py-1 rounded-full w-fit">Pending</span>
                            : item.is_sufficient
                              ? <span className="flex items-center gap-0.5 text-xs font-bold text-[#637C64] bg-[#EDF2EE] px-2 py-1 rounded-full w-fit"><CheckCircle size={10}/>OK</span>
                              : <span className="flex items-center gap-0.5 text-xs font-bold text-[#C2624E] bg-[#FBEAE7] px-2 py-1 rounded-full w-fit"><AlertCircle size={10}/>Low</span>
                          }
                          <input type="number" step="0.001" min="0"
                            className={`border rounded-lg px-2 py-1.5 text-sm w-full outline-none text-right ${isPending ? "border-orange-100 bg-orange-50 cursor-not-allowed text-orange-300" : item.qty_to_issue>item.current_stock ? "border-[#C2624E] bg-[#FBEAE7]/30" : "border-[#E5E1DB] focus:border-[#E8956D]"}`}
                            value={item.qty_to_issue}
                            onChange={e => setBulkItems(b => b.map((x,i) => i===idx ? {...x, qty_to_issue: parseFloat(e.target.value)||0} : x))}
                            disabled={!item.selected || isPending}/>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between bg-[#F4F3F0] rounded-xl px-4 py-3">
                  <div className="text-sm text-[#5C5147]">
                    <span className="font-bold">{bulkItems.filter(i=>i.selected).length}</span> of {bulkItems.length} items selected
                    {bulkItems.some(i=>i.selected && i.qty_to_issue>i.current_stock) && <div className="text-xs text-[#C2624E] font-bold">⚠ Some quantities exceed available stock</div>}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setBulkModal(false)} className="border border-[#E5E1DB] rounded-lg px-4 py-2 text-sm font-bold text-[#5C5147] hover:bg-white">Cancel</button>
                    <button onClick={handleBulkIssue} disabled={bulkIssuing || bulkItems.filter(i=>i.selected).length===0}
                      data-testid="confirm-bulk-issue-btn"
                      className="bg-[#E8956D] text-[#3A322C] rounded-lg px-5 py-2 text-sm font-bold flex items-center gap-2 hover:bg-[#D48661] disabled:opacity-60">
                      {bulkIssuing ? <Loader size={14} className="animate-spin"/> : <Check size={14}/>}
                      Issue {bulkItems.filter(i=>i.selected && i.qty_to_issue>0).length} Items
                    </button>
                  </div>
                </div>
              </>
            )}

            {!bulkLoading && bulkItems.length===0 && !bulkError && selectedKitchen && (
              <div className="text-center py-8 text-[#8A7E73]">
                <UtensilsCrossed size={36} className="mx-auto mb-3 opacity-20"/>
                <p className="font-bold">No daily plan found for {bulkDate}</p>
                <p className="text-sm mt-1">Create a plan in Kitchen &amp; Menu → Daily Plan first.</p>
              </div>
            )}
          </div>
        </Modal>
      )}


      {/* ── DAILY RATION ── */}
      {activeTab === "daily" && (
        <DailyRationTab selectedKitchen={selectedKitchen} user={user} isAdmin={user?.is_admin} isManager={isManager}/>
      )}

      {/* ── REQUISITIONS (planned_offcycle + emergency) ── */}
      {activeTab === "requisitions" && (
        <RequisitionsTab selectedKitchen={selectedKitchen} user={user} isAdmin={user?.is_admin} isManager={isManager}/>
      )}

      {/* ── GATE 2: PENDING CONFIRMATIONS ── */}
      {activeTab === "confirm" && (
        <PendingConfirmationsTab selectedKitchen={selectedKitchen} user={user} isAdmin={user?.is_admin}/>
      )}

      {/* ── GATE 3: STOCK COUNT ── */}
      {activeTab === "stockcount" && (
        <StockCountTab selectedKitchen={selectedKitchen} user={user} isAdmin={user?.is_admin}/>
      )}

      {/* ── VARIANCES REPORT ── */}
      {activeTab === "variances" && (
        <VariancesTab selectedKitchen={selectedKitchen} user={user} isAdmin={user?.is_admin}/>
      )}


            {ConfirmModal}
    </div>
  );
}


// ─── GATE 2: Pending Confirmations Tab ──────────────────────────────────────
function PendingConfirmationsTab({ selectedKitchen, user, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmForm, setConfirmForm] = useState({});
  const [confirming, setConfirming] = useState(null);
  const [msg, setMsg] = useState("");

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await storesAPI.getPendingConfirmations(selectedKitchen ? { kitchen_id: selectedKitchen } : {});
      setItems(r.data || []);
    } catch(e) {}
    setLoading(false);
  };

  useEffect(() => { fetch(); }, [selectedKitchen]); // eslint-disable-line

  const handleConfirm = async (issue) => {
    const qty = parseFloat(confirmForm[issue.id] ?? issue.quantity);
    if (isNaN(qty) || qty < 0) { setMsg("Enter a valid confirmed quantity"); return; }
    setConfirming(issue.id); setMsg("");
    try {
      const r = await storesAPI.confirmIssue(issue.id, { confirmed_qty: qty, notes: "" });
      const v = r.data;
      setMsg(v.has_variance ? `Confirmed with variance: issued ${issue.quantity} vs received ${qty} (diff ${v.variance_qty})` : "Confirmed — quantities match.");
      await fetch();
    } catch(e) { setMsg("Error: " + formatError(e)); }
    setConfirming(null);
  };

  return (
    <div className="space-y-4" data-testid="pending-confirmations-tab">
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800">
        <strong>Gate 2 — Issue Confirmation:</strong> Kitchen staff confirms actual qty received. Any shortfall is flagged as a transfer variance. You cannot confirm your own issue.
      </div>
      {msg && <div className={`rounded-xl px-4 py-3 text-sm font-bold ${msg.startsWith("Error") ? "bg-[#FBEAE7] text-[#C2624E]" : "bg-[#EDF2EE] text-[#637C64]"}`}>{msg}</div>}
      {loading ? <div className="flex justify-center py-10"><RefreshCw size={20} className="animate-spin text-[#E8956D]"/></div>
      : items.length === 0 ? <div className="bg-white border border-[#E5E1DB] rounded-xl p-8 text-center text-[#8A7E73]"><CheckCircle size={36} className="mx-auto mb-3 text-[#637C64]"/><p>No pending confirmations</p></div>
      : (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="grid grid-cols-[1fr_100px_120px_120px_100px] bg-[#F4F3F0] px-4 py-2.5 gap-3 text-xs font-bold uppercase text-[#5C5147]">
            <span>Item</span><span className="text-right">Issued Qty</span><span>Issued By</span><span>Actual Received</span><span>Action</span>
          </div>
          <div className="divide-y divide-[#F4F3F0]">
            {items.map(issue => (
              <div key={issue.id} className="grid grid-cols-[1fr_100px_120px_120px_100px] px-4 py-3 gap-3 items-center">
                <div>
                  <p className="text-sm font-medium text-[#3A322C]">{issue.item_name}</p>
                  <p className="text-xs text-[#8A7E73]">{issue.purpose}</p>
                </div>
                <span className="text-sm font-bold text-[#E8956D] text-right">{issue.quantity} {issue.unit}</span>
                <span className="text-xs text-[#8A7E73]">{issue.created_by_name}</span>
                <input type="number" min="0" step="0.01"
                  className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] w-full"
                  defaultValue={issue.quantity}
                  onChange={e => setConfirmForm(f => ({ ...f, [issue.id]: e.target.value }))}
                  data-testid={`confirm-qty-${issue.id}`}
                />
                <button onClick={() => handleConfirm(issue)} disabled={confirming === issue.id}
                  className="bg-[#637C64] text-white rounded-lg px-3 py-2 text-xs font-bold disabled:opacity-60 flex items-center gap-1"
                  data-testid={`confirm-btn-${issue.id}`}>
                  {confirming === issue.id ? <RefreshCw size={12} className="animate-spin"/> : <CheckCircle size={12}/>} Confirm
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── GATE 3: Stock Count Tab ─────────────────────────────────────────────────
function StockCountTab({ selectedKitchen, user, isAdmin }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [variances, setVariances] = useState([]);
  const [approving, setApproving] = useState(null);
  const [approveReason, setApproveReason] = useState({});

  useEffect(() => {
    Promise.all([
      adminAPI.getMasterItems ? adminAPI.getMasterItems({}).then(r => setItems(r.data || [])).catch(()=>{}) : Promise.resolve(),
      storesAPI.getStockCountVariances({ status: "pending_approval" }).then(r => setVariances(r.data || [])).catch(()=>{})
    ]).finally(() => setLoading(false));
  }, [selectedKitchen]); // eslint-disable-line

  const handleSubmit = async () => {
    const countItems = Object.entries(counts).filter(([,v]) => v !== "").map(([itemId, qty]) => ({ item_id: itemId, counted_qty: parseFloat(qty)||0 }));
    if (!countItems.length) { setMsg("Enter counts for at least one item"); return; }
    setSubmitting(true); setMsg("");
    try {
      const r = await storesAPI.submitStockCount({ kitchen_id: selectedKitchen||"", location: "main_store", items: countItems });
      setMsg(r.data.message);
      const rv = await storesAPI.getStockCountVariances({ status: "pending_approval" });
      setVariances(rv.data || []);
      setCounts({});
    } catch(e) { setMsg("Error: " + formatError(e)); }
    setSubmitting(false);
  };

  const handleApprove = async (varId) => {
    const reason = approveReason[varId];
    if (!reason?.trim()) { setMsg("Enter a reason for this adjustment"); return; }
    setApproving(varId);
    try {
      const r = await storesAPI.approveCountVariance(varId, { reason });
      setMsg(r.data.message);
      const rv = await storesAPI.getStockCountVariances({ status: "pending_approval" });
      setVariances(rv.data || []);
    } catch(e) { setMsg("Error: " + formatError(e)); }
    setApproving(null);
  };

  return (
    <div className="space-y-4" data-testid="stock-count-tab">
      <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 text-sm text-purple-800">
        <strong>Gate 3 — Physical Reconciliation:</strong> Enter physical counts. Book balance shown for comparison. Variances are logged but do NOT update book stock until an Admin approves the adjustment with a reason.
      </div>
      {msg && <div className={`rounded-xl px-4 py-3 text-sm font-bold ${msg.startsWith("Error") ? "bg-[#FBEAE7] text-[#C2624E]" : "bg-[#EDF2EE] text-[#637C64]"}`}>{msg}</div>}

      <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F4F3F0] font-bold text-sm text-[#3A322C]">Enter Physical Counts</div>
        <div className="max-h-[350px] overflow-y-auto divide-y divide-[#F4F3F0]">
          {items.slice(0, 30).map(item => (
            <div key={item.id} className="flex items-center px-5 py-3 gap-4">
              <div className="flex-1 text-sm font-medium text-[#3A322C]">{item.name}</div>
              <span className="text-xs text-[#8A7E73] w-16">{item.unit}</span>
              <input type="number" min="0" step="0.01" placeholder="Count…"
                className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm w-28 outline-none focus:border-[#E8956D] text-right"
                value={counts[item.id] || ""}
                onChange={e => setCounts(c => ({ ...c, [item.id]: e.target.value }))}
                data-testid={`count-${item.id}`}
              />
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-[#F4F3F0]">
          <button onClick={handleSubmit} disabled={submitting}
            className="bg-[#E8956D] text-[#3A322C] rounded-lg px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-60">
            {submitting ? <RefreshCw size={14} className="animate-spin"/> : <BarChart2 size={14}/>}
            Submit Count
          </button>
        </div>
      </div>

      {/* Pending variances — admin approval required */}
      {variances.length > 0 && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#F4F3F0] flex items-center justify-between">
            <p className="font-bold text-[#3A322C] text-sm">{variances.length} Variance(s) Pending Admin Approval</p>
          </div>
          <div className="divide-y divide-[#F4F3F0]">
            {variances.map(v => (
              <div key={v.id} className="px-5 py-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-[#3A322C]">{v.item_name}</p>
                    <p className="text-xs text-[#8A7E73]">
                      Book: <strong>{v.book_qty}</strong> {v.unit} → Counted: <strong>{v.counted_qty}</strong> {v.unit}
                      <span className={`ml-2 font-bold ${v.variance_qty > 0 ? "text-[#637C64]" : "text-[#C2624E]"}`}>
                        ({v.variance_qty > 0 ? "+" : ""}{v.variance_qty} {v.unit})
                      </span>
                    </p>
                    <p className="text-xs text-[#8A7E73]">Counted by {v.counted_by_name} on {v.count_date}</p>
                  </div>
                  <span className="text-xs bg-amber-100 text-amber-700 font-bold px-2 py-1 rounded-full">Pending</span>
                </div>
                {isAdmin && (
                  <div className="flex gap-2">
                    <input className="flex-1 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none"
                      placeholder="Reason for adjustment (required)…"
                      value={approveReason[v.id] || ""}
                      onChange={e => setApproveReason(r => ({ ...r, [v.id]: e.target.value }))}
                      data-testid={`approve-reason-${v.id}`}
                    />
                    <button onClick={() => handleApprove(v.id)} disabled={approving === v.id}
                      className="bg-[#637C64] text-white rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-60"
                      data-testid={`approve-variance-${v.id}`}>
                      {approving === v.id ? <RefreshCw size={14} className="animate-spin"/> : "Approve"}
                    </button>
                  </div>
                )}
                {!isAdmin && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">Admin approval required before book stock is adjusted.</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Variances Report Tab ─────────────────────────────────────────────────────
function VariancesTab({ selectedKitchen, user, isAdmin }) {
  const [receivingReport, setReceivingReport] = useState(null);
  const [transferVars, setTransferVars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState("receiving");

  useEffect(() => {
    const params = selectedKitchen ? { kitchen_id: selectedKitchen } : {};
    Promise.all([
      storesAPI.getReceivingVarianceReport(params).then(r => setReceivingReport(r.data)).catch(()=>{}),
      storesAPI.getTransferVariances(params).then(r => setTransferVars(r.data || [])).catch(()=>{}),
    ]).finally(() => setLoading(false));
  }, [selectedKitchen]); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="variances-tab">
      <div className="flex gap-2 border-b border-[#E5E1DB] pb-1">
        {[["receiving","Gate 1: Receiving"],["transfer","Gate 2: Transfer"]].map(([v,l]) => (
          <button key={v} onClick={() => setSub(v)}
            className={`px-4 py-2 text-sm font-semibold -mb-px ${sub===v ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73]"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-8"><RefreshCw size={20} className="animate-spin text-[#E8956D]"/></div> : (
        <>
          {sub === "receiving" && receivingReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 text-center">
                  <p className="text-3xl font-extrabold text-[#C2624E]">{receivingReport.unverified_receipt_count}</p>
                  <p className="text-xs text-[#8A7E73] mt-1">Unverified Receipts (no PO)</p>
                </div>
                <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 text-center">
                  <p className="text-3xl font-extrabold text-amber-600">{receivingReport.quantity_variances?.length || 0}</p>
                  <p className="text-xs text-[#8A7E73] mt-1">Quantity Variances vs PO</p>
                </div>
              </div>
              {receivingReport.quantity_variances?.length > 0 && (
                <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#F4F3F0] font-bold text-sm text-[#3A322C]">PO Quantity Variances by Supplier</div>
                  <div className="divide-y divide-[#F4F3F0]">
                    {receivingReport.quantity_variances.map((v,i) => (
                      <div key={i} className="px-4 py-3 flex items-center gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-[#3A322C]">{v.item_name}</p>
                          <p className="text-xs text-[#8A7E73]">{v.supplier} · PO {v.po_number}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-[#C2624E]">PO: {v.po_qty} → Rcvd: {v.received_qty}</p>
                          <p className="text-xs text-[#8A7E73]">{v.variance_pct}% variance</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {sub === "transfer" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-[#F4F3F0] font-bold text-sm text-[#3A322C]">Transfer Variances (issued ≠ confirmed)</div>
              {transferVars.length === 0 ? <div className="p-6 text-center text-[#8A7E73]"><CheckCircle size={28} className="mx-auto mb-2 text-[#637C64]"/><p>No transfer variances</p></div>
              : <div className="divide-y divide-[#F4F3F0]">
                  {transferVars.map((v,i) => (
                    <div key={i} className="px-4 py-3 flex items-center gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#3A322C]">{v.item_name}</p>
                        <p className="text-xs text-[#8A7E73]">Issued by {v.issued_by_name} · Confirmed by {v.confirmed_by_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-[#C2624E]">Issued {v.issued_qty} → Confirmed {v.confirmed_qty}</p>
                        <p className="text-xs text-[#8A7E73]">Variance: {v.variance_qty}</p>
                      </div>
                    </div>
                  ))}
                </div>
              }
            </div>
          )}
        </>
      )}
    </div>
  );
}



// ─── DAILY RATION REQUEST TAB ─────────────────────────────────────────────────
// Endpoint: POST /stores/requests (request_type="daily"), GET /stores/requests
function DailyRationTab({ selectedKitchen, user, isManager }) {
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([{ item_id: "", quantity: "" }]);
  const [reason, setReason] = useState("Daily kitchen ration");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ text: "", ok: true });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    adminAPI.getMasterItems({}).then(r => setItems(r.data || [])).catch(() => {});
    loadTodayRequests();
  }, [selectedKitchen]); // eslint-disable-line

  const loadTodayRequests = async () => {
    setLoading(true);
    try {
      // GET /stores/requests?request_type=daily&kitchen_id=...
      const params = { request_type: "daily" };
      if (selectedKitchen) params.kitchen_id = selectedKitchen;
      const r = await storesAPI.getRequests(params);
      // Filter to today only (backend doesn't date-filter; client-side is fine for daily view)
      const todayItems = (r.data || []).filter(req => {
        const d = (req.created_at || "").slice(0, 10);
        return d === today;
      });
      setRequests(todayItems);
    } catch (e) { }
    setLoading(false);
  };

  const addRow = () => setRows(r => [...r, { item_id: "", quantity: "" }]);
  const removeRow = (i) => setRows(r => r.filter((_, j) => j !== i));
  const updateRow = (i, field, val) => setRows(r => r.map((row, j) => j === i ? { ...row, [field]: val } : row));

  const handleSubmit = async () => {
    const valid = rows.filter(r => r.item_id && parseFloat(r.quantity) > 0);
    if (!valid.length) { setMsg({ text: "Add at least one item with a quantity", ok: false }); return; }
    setSubmitting(true); setMsg({ text: "", ok: true });
    const errors = [];
    let created = 0;
    for (const row of valid) {
      try {
        // POST /stores/requests  (body: item_id, quantity, request_type, reason, kitchen_id)
        await storesAPI.createRequest({
          item_id: row.item_id,
          quantity: parseFloat(row.quantity),
          request_type: "daily",
          reason,
          kitchen_id: selectedKitchen || "",
          needed_by: today,
        });
        created++;
      } catch (e) {
        errors.push(formatError(e));
      }
    }
    setMsg({ text: errors.length ? `${created} created, errors: ${errors[0]}` : `${created} daily request(s) submitted`, ok: !errors.length });
    setRows([{ item_id: "", quantity: "" }]);
    await loadTodayRequests();
    setSubmitting(false);
  };

  // Storekeeper issues: POST /stores/requests/{id}/fulfill
  const handleFulfill = async (req) => {
    try {
      await storesAPI.fulfillRequest(req.id, { quantity: req.quantity });
      setMsg({ text: `Issued: ${req.item_name} × ${req.quantity}`, ok: true });
      await loadTodayRequests();
    } catch (e) { setMsg({ text: formatError(e), ok: false }); }
  };

  const STATUS_STYLE = {
    approved:   "bg-green-100 text-green-700",
    fulfilled:  "bg-blue-100 text-blue-700",
    confirmed:  "bg-[#EDF2EE] text-[#637C64]",
    pending:    "bg-amber-100 text-amber-700",
    rejected:   "bg-red-100 text-red-700",
  };
  const STATUS_LABEL = { approved: "Ready to Issue", fulfilled: "Issued (pending confirm)", confirmed: "Confirmed", pending: "Pending", rejected: "Rejected" };

  const isStorekeeper = user?.is_admin || isManager || ["Store Keeper"].includes(user?.role_name || "");

  return (
    <div className="space-y-5" data-testid="daily-ration-tab">
      {/* Request form */}
      <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-bold text-[#3A322C]">Daily Ration Request</h2>
            <p className="text-xs text-[#8A7E73] mt-0.5">
              Calls <code className="bg-[#F4F3F0] px-1 rounded">POST /stores/requests</code> with <code className="bg-[#F4F3F0] px-1 rounded">request_type="daily"</code>
            </p>
          </div>
          <span className="text-xs bg-green-100 text-green-700 font-bold px-2 py-1 rounded-full">No approval needed</span>
        </div>

        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="flex gap-2 items-center">
              <select className="flex-1 border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                value={row.item_id} onChange={e => updateRow(i, "item_id", e.target.value)}
                data-testid={`item-select-${i}`}>
                <option value="">Select item…</option>
                {items.map(it => <option key={it.id} value={it.id}>{it.name} ({it.unit})</option>)}
              </select>
              <input type="number" min="0.01" step="0.01" placeholder="Qty"
                className="w-24 border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] text-right"
                value={row.quantity} onChange={e => updateRow(i, "quantity", e.target.value)}
                data-testid={`qty-input-${i}`}/>
              {rows.length > 1 && (
                <button onClick={() => removeRow(i)} className="text-[#C4BAAF] hover:text-[#C2624E] p-1"><X size={14}/></button>
              )}
            </div>
          ))}
          <button onClick={addRow} className="text-sm text-[#E8956D] font-bold hover:underline flex items-center gap-1">
            <Plus size={14}/> Add item
          </button>
        </div>

        <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
          placeholder="Reason / notes"
          value={reason} onChange={e => setReason(e.target.value)}/>

        {msg.text && (
          <div className={`text-sm px-4 py-2.5 rounded-xl font-medium ${msg.ok ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
            {msg.text}
          </div>
        )}

        <button onClick={handleSubmit} disabled={submitting}
          className="bg-[#E8956D] text-white rounded-xl px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-60"
          data-testid="submit-daily-btn">
          {submitting ? <RefreshCw size={14} className="animate-spin"/> : <ClipboardList size={14}/>}
          {submitting ? "Submitting…" : "Submit Daily Request"}
        </button>
      </div>

      {/* Today's requests */}
      <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F4F3F0] flex items-center justify-between">
          <p className="font-bold text-[#3A322C] text-sm">Today's Daily Requests</p>
          <button onClick={loadTodayRequests} className="text-[#E8956D]"><RefreshCw size={14}/></button>
        </div>
        {loading ? <div className="flex justify-center py-8"><RefreshCw size={20} className="animate-spin text-[#E8956D]"/></div>
        : requests.length === 0 ? <div className="p-6 text-center text-[#8A7E73] text-sm">No daily requests today yet.</div>
        : (
          <div className="divide-y divide-[#F4F3F0]">
            {requests.map(req => (
              <div key={req.id} className="px-5 py-3 flex items-center gap-3" data-testid={`daily-req-${req.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#3A322C] truncate">{req.item_name}</p>
                  <p className="text-xs text-[#8A7E73]">{req.quantity} {req.unit} · {req.requested_by_name}</p>
                  {req.issue_id && <p className="text-[10px] text-[#8A7E73] font-mono">issue: {req.issue_id?.slice(0,16)}</p>}
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_STYLE[req.status] || "bg-[#F4F3F0] text-[#5C5147]"}`}>
                  {STATUS_LABEL[req.status] || req.status}
                </span>
                {/* Storekeeper issues: POST /stores/requests/{id}/fulfill */}
                {isStorekeeper && req.status === "approved" && (
                  <button onClick={() => handleFulfill(req)}
                    className="text-xs bg-[#E8956D] text-white px-3 py-1.5 rounded-lg font-bold flex-shrink-0"
                    data-testid={`fulfill-btn-${req.id}`}>
                    Issue
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── REQUISITIONS TAB (planned_offcycle + emergency) ─────────────────────────
// Endpoints used:
//   POST /stores/requests              — raise request
//   GET  /stores/requests              — list (emergency first)
//   POST /stores/requests/{id}/approve — approve
//   POST /stores/requests/{id}/reject  — reject
//   POST /stores/requests/{id}/fulfill — issue against approved request
function RequisitionsTab({ selectedKitchen, user, isManager, isAdmin }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ item_id: "", quantity: "", request_type: "planned_offcycle", reason: "", needed_by: "" });
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ text: "", ok: true });
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actioning, setActioning] = useState(null);

  const canApprove = isAdmin || isManager;
  const canFulfill = isAdmin || isManager || ["Store Keeper"].includes(user?.role_name || "");

  useEffect(() => {
    adminAPI.getMasterItems({}).then(r => setItems(r.data || [])).catch(() => {});
    loadRequests();
  }, [selectedKitchen]); // eslint-disable-line

  const loadRequests = async () => {
    setLoading(true);
    try {
      // GET /stores/requests (excludes daily — filter client-side)
      const params = {};
      if (selectedKitchen) params.kitchen_id = selectedKitchen;
      const r = await storesAPI.getRequests(params);
      const nonDaily = (r.data || []).filter(req => req.request_type !== "daily");
      setRequests(nonDaily); // backend already returns emergency first
    } catch (e) { }
    setLoading(false);
  };

  // POST /stores/requests
  const handleCreate = async () => {
    if (!form.item_id || !form.quantity || !form.reason.trim()) {
      setMsg({ text: "Item, quantity, and reason are required", ok: false }); return;
    }
    setSubmitting(true); setMsg({ text: "", ok: true });
    try {
      await storesAPI.createRequest({
        item_id: form.item_id,
        quantity: parseFloat(form.quantity),
        request_type: form.request_type,
        reason: form.reason,
        needed_by: form.needed_by,
        kitchen_id: selectedKitchen || "",
      });
      setMsg({ text: "Request submitted!", ok: true });
      setForm({ item_id: "", quantity: "", request_type: "planned_offcycle", reason: "", needed_by: "" });
      setShowForm(false);
      await loadRequests();
    } catch (e) { setMsg({ text: formatError(e), ok: false }); }
    setSubmitting(false);
  };

  // POST /stores/requests/{id}/approve
  const handleApprove = async (req) => {
    setActioning(req.id);
    try {
      await storesAPI.approveRequest(req.id);
      setMsg({ text: `Approved: ${req.item_name}`, ok: true });
      await loadRequests();
    } catch (e) { setMsg({ text: formatError(e), ok: false }); }
    setActioning(null);
  };

  // POST /stores/requests/{id}/reject
  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setActioning(rejectModal.id);
    try {
      await storesAPI.rejectRequest(rejectModal.id, { reason: rejectReason });
      setMsg({ text: `Rejected: ${rejectModal.item_name}`, ok: true });
      setRejectModal(null); setRejectReason("");
      await loadRequests();
    } catch (e) { setMsg({ text: formatError(e), ok: false }); }
    setActioning(null);
  };

  // POST /stores/requests/{id}/fulfill
  const handleFulfill = async (req) => {
    setActioning(req.id);
    try {
      await storesAPI.fulfillRequest(req.id, { quantity: req.quantity });
      setMsg({ text: `Issued: ${req.item_name} × ${req.quantity}`, ok: true });
      await loadRequests();
    } catch (e) { setMsg({ text: formatError(e), ok: false }); }
    setActioning(null);
  };

  const TYPE_STYLE = { emergency: "bg-red-100 text-red-700", planned_offcycle: "bg-purple-100 text-purple-700" };
  const STATUS_STYLE = {
    approved:  "bg-green-100 text-green-700",
    fulfilled: "bg-blue-100 text-blue-700",
    confirmed: "bg-[#EDF2EE] text-[#637C64]",
    pending:   "bg-amber-100 text-amber-700",
    rejected:  "bg-red-100 text-red-700",
  };

  const pending   = requests.filter(r => r.status === "pending");
  const approved  = requests.filter(r => r.status === "approved");
  const history   = requests.filter(r => !["pending","approved"].includes(r.status));

  return (
    <div className="space-y-5" data-testid="requisitions-tab">
      {/* Create button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-[#3A322C]">Planned & Emergency Requisitions</h2>
          <p className="text-xs text-[#8A7E73] mt-0.5">
            Calls <code className="bg-[#F4F3F0] px-1 rounded">POST /stores/requests</code> — requires approval before issue
          </p>
        </div>
        <button onClick={() => { setShowForm(!showForm); setMsg({ text:"", ok:true }); }}
          className="bg-[#E8956D] text-white rounded-xl px-4 py-2 text-sm font-bold flex items-center gap-2">
          <Plus size={14}/> New Request
        </button>
      </div>

      {/* New request form */}
      {showForm && (
        <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-4">
          <div className="flex gap-2">
            {[["planned_offcycle","Planned Off-Cycle"],["emergency","Emergency"]].map(([v,l]) => (
              <button key={v} onClick={() => setForm(f => ({ ...f, request_type: v }))}
                className={`flex-1 py-2.5 rounded-xl text-sm font-bold border-2 ${form.request_type===v ? (v==="emergency"?"border-red-500 bg-red-50 text-red-700":"border-purple-400 bg-purple-50 text-purple-700") : "border-[#E5E1DB] text-[#5C5147]"}`}
                data-testid={`type-${v}`}>
                {v==="emergency" ? "🚨 " : ""}{l}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                value={form.item_id} onChange={e => setForm(f => ({ ...f, item_id: e.target.value }))}
                data-testid="req-item-select">
                <option value="">Select item…</option>
                {items.map(it => <option key={it.id} value={it.id}>{it.name} ({it.unit})</option>)}
              </select>
            </div>
            <input type="number" min="0.01" step="0.01" placeholder="Quantity"
              className="border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
              value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}/>
            <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
              value={form.needed_by} onChange={e => setForm(f => ({ ...f, needed_by: e.target.value }))}/>
          </div>
          <textarea rows={2} placeholder="Reason (required) *"
            className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
            value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}/>
          {msg.text && <div className={`text-sm px-4 py-2 rounded-xl ${msg.ok?"bg-[#EDF2EE] text-[#637C64]":"bg-[#FBEAE7] text-[#C2624E]"}`}>{msg.text}</div>}
          <div className="flex gap-3">
            <button onClick={() => setShowForm(false)} className="flex-1 border border-[#E5E1DB] rounded-xl py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
            <button onClick={handleCreate} disabled={submitting}
              className={`flex-1 rounded-xl py-2.5 text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-60 ${form.request_type==="emergency"?"bg-red-600 hover:bg-red-700":"bg-purple-600 hover:bg-purple-700"}`}
              data-testid="submit-req-btn">
              {submitting ? <RefreshCw size={14} className="animate-spin"/> : <ClipboardList size={14}/>}
              Submit {form.request_type==="emergency"?"Emergency":"Planned"} Request
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && <div className="flex justify-center py-8"><RefreshCw size={22} className="animate-spin text-[#E8956D]"/></div>}

      {/* Pending approval queue */}
      {!loading && (
        <>
          <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#F4F3F0] bg-amber-50">
              <p className="font-bold text-amber-800 text-sm">
                Pending Approval ({pending.length})
                {canApprove && " — you can approve/reject"}
              </p>
              <p className="text-[10px] text-amber-600 mt-0.5">
                Approve: <code>POST /stores/requests/{"{id}"}/approve</code> · Reject: <code>POST /stores/requests/{"{id}"}/reject</code>
              </p>
            </div>
            {pending.length === 0 ? <div className="p-5 text-center text-[#8A7E73] text-sm">No pending requests.</div> : (
              <div className="divide-y divide-[#F4F3F0]">
                {pending.map(req => (
                  <div key={req.id} className={`px-5 py-4 flex items-start gap-3 ${req.request_type==="emergency"?"bg-red-50/50":""}`}
                    data-testid={`pending-req-${req.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${TYPE_STYLE[req.request_type]}`}>
                          {req.request_type==="emergency"?"🚨 EMERGENCY":"Planned"}
                        </span>
                        <p className="text-sm font-bold text-[#3A322C]">{req.item_name}</p>
                        <span className="text-xs text-[#8A7E73]">{req.quantity} {req.unit}</span>
                      </div>
                      <p className="text-xs text-[#8A7E73] mt-0.5">{req.reason}</p>
                      <p className="text-xs text-[#8A7E73]">By {req.requested_by_name} · {req.needed_by ? `Needed: ${req.needed_by}` : ""}</p>
                    </div>
                    {canApprove && (
                      <div className="flex gap-2 flex-shrink-0">
                        <button onClick={() => handleApprove(req)}
                          disabled={actioning===req.id}
                          className="bg-[#637C64] text-white rounded-lg px-3 py-1.5 text-xs font-bold disabled:opacity-60"
                          data-testid={`approve-btn-${req.id}`}>
                          {actioning===req.id ? <RefreshCw size={12} className="animate-spin"/> : "Approve"}
                        </button>
                        <button onClick={() => setRejectModal(req)}
                          className="border border-[#C2624E] text-[#C2624E] rounded-lg px-3 py-1.5 text-xs font-bold"
                          data-testid={`reject-btn-${req.id}`}>
                          Reject
                        </button>
                      </div>
                    )}
                    {!canApprove && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full font-bold flex-shrink-0">Awaiting approval</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Approved — ready to issue */}
          <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#F4F3F0] bg-green-50">
              <p className="font-bold text-green-800 text-sm">Approved — Ready to Issue ({approved.length})</p>
              <p className="text-[10px] text-green-600 mt-0.5">Issue: <code>POST /stores/requests/{"{id}"}/fulfill</code></p>
            </div>
            {approved.length === 0 ? <div className="p-5 text-center text-[#8A7E73] text-sm">No approved requests waiting.</div> : (
              <div className="divide-y divide-[#F4F3F0]">
                {approved.map(req => (
                  <div key={req.id} className="px-5 py-4 flex items-center gap-3" data-testid={`approved-req-${req.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${TYPE_STYLE[req.request_type]}`}>
                          {req.request_type==="emergency"?"🚨 EMERGENCY":"Planned"}
                        </span>
                        <p className="text-sm font-bold text-[#3A322C]">{req.item_name}</p>
                        <span className="text-xs text-[#8A7E73]">{req.quantity} {req.unit}</span>
                      </div>
                      <p className="text-xs text-[#8A7E73]">Approved by {req.approved_by_name}</p>
                    </div>
                    {canFulfill ? (
                      <button onClick={() => handleFulfill(req)}
                        disabled={actioning===req.id}
                        className="bg-[#E8956D] text-white rounded-lg px-4 py-2 text-xs font-bold disabled:opacity-60 flex-shrink-0 flex items-center gap-1"
                        data-testid={`fulfill-btn-${req.id}`}>
                        {actioning===req.id ? <RefreshCw size={12} className="animate-spin"/> : null}
                        Issue Stock
                      </button>
                    ) : (
                      <span className="text-xs text-[#8A7E73] flex-shrink-0">Storekeeper will issue</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-[#F4F3F0]">
                <p className="font-bold text-[#3A322C] text-sm">History ({history.length})</p>
              </div>
              <div className="divide-y divide-[#F4F3F0] max-h-64 overflow-y-auto">
                {history.map(req => (
                  <div key={req.id} className="px-5 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${TYPE_STYLE[req.request_type]}`}>
                          {req.request_type==="emergency"?"🚨 EMERG":"Planned"}
                        </span>
                        <p className="text-sm font-medium text-[#3A322C] truncate">{req.item_name}</p>
                        <span className="text-xs text-[#8A7E73]">{req.quantity} {req.unit}</span>
                      </div>
                      {req.issue_id && <p className="text-[10px] text-[#8A7E73] mt-0.5 font-mono">issue: {req.issue_id?.slice(0,16)}</p>}
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_STYLE[req.status]||"bg-[#F4F3F0] text-[#5C5147]"}`}>
                      {req.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {msg.text && !showForm && (
        <div className={`text-sm px-4 py-3 rounded-xl font-medium ${msg.ok?"bg-[#EDF2EE] text-[#637C64]":"bg-[#FBEAE7] text-[#C2624E]"}`}>{msg.text}</div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="font-bold text-[#3A322C]">Reject: {rejectModal.item_name}</h3>
            <textarea rows={3} className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#C2624E] resize-none"
              placeholder="Reason for rejection (required)…"
              value={rejectReason} onChange={e => setRejectReason(e.target.value)}/>
            <div className="flex gap-3">
              <button onClick={() => { setRejectModal(null); setRejectReason(""); }} className="flex-1 border border-[#E5E1DB] rounded-xl py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleReject} disabled={!rejectReason.trim() || actioning===rejectModal.id}
                className="flex-1 bg-[#C2624E] text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-60"
                data-testid="confirm-reject-btn">
                {actioning===rejectModal.id ? "Rejecting…" : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

